import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { applicationsApi, jobsApi, tasksApi, ApiError } from "@/api";
import type {
  OpenApplicationRow,
  OpenApplicationsResponse,
  LetterSource,
  JobApplication,
  DrawingSet,
} from "@/types";
import { PLANNING_STATUS_OPTIONS, BC_STATUS_OPTIONS } from "@/types";
import { Button, Spinner, Pill, SelectField, Toast, useToast } from "@/components/ui";
import { decisionTone, humanise, workstreamTypeLabel, buildJobAddressString, isContainerSet } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { DeadlineChipView } from "./JobChips";
import { AppLetters } from "./AppLetters";
import { LetterDetailDrawer } from "./LetterDetailDrawer";
import { EditApplicationDrawer } from "./EditApplicationDrawer";
import { PlanWirePanel, isPlanWireRelevant } from "./PlanWirePanel";
import "./jobs.css";
import "./open-applications.css";

type ClosePrompt = { appId: number; tasks: { id: number; title: string; priority: string; due_date: string | null }[] };
type EditTarget = { app: JobApplication; jobNumber: number; drawingSets: DrawingSet[] };

/** Jobs › Open applications — a cross-job view of every still-open application,
 *  split Planning-first then Building control. Each card leads with the parent
 *  job (a link to its detail) and surfaces the LPA/BCB + status; status is
 *  editable inline, the reference can be amended via the shared Edit-application
 *  drawer, and an application can be closed (with the same cancel-linked-tasks
 *  prompt the job-detail tab uses). Letters expand inline via the very same
 *  opening-link the job-detail card uses ({@link AppLetters} →
 *  {@link LetterDetailDrawer}) — shown only when the row actually has letters.
 *  The feed comes from a single GET /open_applications; writes update the list in
 *  place (closed apps drop out, since the page only lists open ones). */
export function OpenApplications() {
  const [data, setData] = useState<OpenApplicationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [closePrompt, setClosePrompt] = useState<ClosePrompt | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [openLetter, setOpenLetter] = useState<{ id: number; source: LetterSource } | null>(null);

  const { toast, showToast } = useToast();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    applicationsApi
      .listOpenApplications()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load open applications.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  /** Silent re-fetch (no full-page spinner) — used after an edit so the amended
   *  reference + any server recompute show without resetting the page. Cards keep
   *  their keys, so expanded letters survive. */
  const refresh = useCallback(() => {
    applicationsApi
      .listOpenApplications()
      .then((res) => setData(res))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not refresh the list."));
  }, []);

  /** Optimistic status edit — set_status never closes, so the row stays on the
   *  page; just reflect the new status in place. */
  function applyStatus(id: number, next: string) {
    setData((prev) => {
      if (!prev) return prev;
      const map = (rows: OpenApplicationRow[]) => rows.map((r) => (r.id === id ? { ...r, status: next } : r));
      return { ...prev, planning: map(prev.planning), building_control: map(prev.building_control) };
    });
  }

  /** Drop a (now-closed) application from both buckets and re-derive the counts —
   *  the honest optimistic update for a list that only shows open applications. */
  function dropRow(id: number) {
    setData((prev) => {
      if (!prev) return prev;
      const planning = prev.planning.filter((r) => r.id !== id);
      const building_control = prev.building_control.filter((r) => r.id !== id);
      return {
        planning,
        building_control,
        counts: { planning: planning.length, building_control: building_control.length, total: planning.length + building_control.length },
      };
    });
  }

  async function changeStatus(app: OpenApplicationRow, next: string) {
    if (busyId != null || next === app.status) return;
    setBusyId(app.id);
    try {
      await applicationsApi.setApplicationStatus(app.id, next);
      applyStatus(app.id, next);
      showToast(`Status: ${humanise(app.status)} → ${humanise(next)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the status.");
    } finally {
      setBusyId(null);
    }
  }

  /** Open the shared Edit-application drawer. The drawer needs the job's drawing
   *  sets + the full application record, so fetch the job /detail on demand
   *  (this page's feed is a lean projection). */
  async function openEdit(app: OpenApplicationRow) {
    if (editLoadingId != null || !app.job) return;
    setEditLoadingId(app.id);
    setError(undefined);
    try {
      const detail = await jobsApi.getJobDetail(app.job.id);
      const full = detail.applications.find((a) => a.id === app.id);
      if (!full) throw new ApiError(404, "That application is no longer on the job.");
      setEditTarget({
        app: full,
        jobNumber: detail.job.job_number,
        // Container sets (portal document files) never feed the drawing picker.
        drawingSets: detail.drawing_sets.filter((s) => !isContainerSet(s)),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not open the application for editing.");
    } finally {
      setEditLoadingId(null);
    }
  }

  async function close(app: OpenApplicationRow) {
    if (busyId != null) return;
    if (!window.confirm(`Close application ${app.app_reference || "(draft)"}? It drops out of deadline tracking.`)) return;
    setBusyId(app.id);
    try {
      const res = await applicationsApi.closeApplication(app.id);
      dropRow(app.id);
      showToast("Application closed.");
      if (res.linked_open_tasks_count > 0) {
        setClosePrompt({ appId: app.id, tasks: res.linked_open_tasks });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not close the application.");
    } finally {
      setBusyId(null);
    }
  }

  async function cancelLinkedTasks() {
    if (!closePrompt || cancelling) return;
    setCancelling(true);
    try {
      for (const t of closePrompt.tasks) {
        await tasksApi.updateTask({ id: t.id, status: "cancelled" });
      }
      showToast(`Cancelled ${closePrompt.tasks.length} linked task${closePrompt.tasks.length === 1 ? "" : "s"}.`);
      setClosePrompt(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not cancel the linked tasks.");
    } finally {
      setCancelling(false);
    }
  }

  const counts = data?.counts;

  return (
    <section className="open-apps">
      <Link to="/staff/jobs" className="open-apps__back">← Jobs</Link>

      <header className="open-apps__head">
        <h1>Open applications</h1>
        <p className="open-apps__sub">
          {counts ? (
            <>
              {counts.total} open · {counts.planning} planning · {counts.building_control} building control
            </>
          ) : (
            "Every application still in flight, across all jobs."
          )}
        </p>
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="open-apps__loading">
          <Spinner label="Loading open applications…" />
        </div>
      ) : !data || data.counts.total === 0 ? (
        <p className="open-apps__empty">No open applications right now.</p>
      ) : (
        <>
          <ApplicationGroup
            title="Planning"
            rows={data.planning}
            emptyLabel="No open planning applications."
            busyId={busyId}
            editLoadingId={editLoadingId}
            onStatus={changeStatus}
            onEdit={openEdit}
            onClose={close}
            onOpenLetter={(id, source) => setOpenLetter({ id, source })}
          />
          <ApplicationGroup
            title="Building control"
            rows={data.building_control}
            emptyLabel="No open building control applications."
            busyId={busyId}
            editLoadingId={editLoadingId}
            onStatus={changeStatus}
            onEdit={openEdit}
            onClose={close}
            onOpenLetter={(id, source) => setOpenLetter({ id, source })}
          />
        </>
      )}

      {editTarget ? (
        <EditApplicationDrawer
          app={editTarget.app}
          jobNumber={editTarget.jobNumber}
          drawingSets={editTarget.drawingSets}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            showToast("Application updated.");
            refresh();
          }}
        />
      ) : null}

      {closePrompt ? (
        <div className="job-modal__overlay" onClick={cancelling ? undefined : () => setClosePrompt(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Cancel linked tasks">
            <header className="job-modal__head"><h3>Cancel linked tasks?</h3></header>
            <div className="job-modal__body">
              <p className="job-drawer__muted">
                This application had {closePrompt.tasks.length} open task{closePrompt.tasks.length === 1 ? "" : "s"}. Cancel
                {closePrompt.tasks.length === 1 ? " it" : " them"} too, or keep
                {closePrompt.tasks.length === 1 ? " it" : " them"} on the job?
              </p>
              <ul className="job-modal__tasks">
                {closePrompt.tasks.map((t) => (
                  <li key={t.id}>
                    <span className="job-task__title">{t.title}</span>
                    <span className="job-drawer__muted">
                      {humanise(t.priority)}{t.due_date ? ` · due ${formatDateShort(t.due_date)}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <footer className="job-modal__foot">
              <Button variant="ghost" onClick={() => setClosePrompt(null)} disabled={cancelling}>Keep them</Button>
              <Button variant="primary" onClick={cancelLinkedTasks} loading={cancelling}>Cancel these tasks</Button>
            </footer>
          </div>
        </div>
      ) : null}

      {openLetter ? (
        <LetterDetailDrawer id={openLetter.id} source={openLetter.source} onClose={() => setOpenLetter(null)} />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

function ApplicationGroup({
  title,
  rows,
  emptyLabel,
  busyId,
  editLoadingId,
  onStatus,
  onEdit,
  onClose,
  onOpenLetter,
}: {
  title: string;
  rows: OpenApplicationRow[];
  emptyLabel: string;
  busyId: number | null;
  editLoadingId: number | null;
  onStatus: (app: OpenApplicationRow, next: string) => void;
  onEdit: (app: OpenApplicationRow) => void;
  onClose: (app: OpenApplicationRow) => void;
  onOpenLetter: (id: number, source: LetterSource) => void;
}) {
  return (
    <section className="open-apps__group">
      <h2 className="open-apps__group-title">
        {title} <span className="open-apps__count">{rows.length}</span>
      </h2>
      {rows.length === 0 ? (
        <p className="open-apps__muted">{emptyLabel}</p>
      ) : (
        <div className="open-apps__list">
          {rows.map((app) => (
            <OpenApplicationCard
              key={app.id}
              app={app}
              busy={busyId === app.id}
              editLoading={editLoadingId === app.id}
              onStatus={(next) => onStatus(app, next)}
              onEdit={() => onEdit(app)}
              onClose={() => onClose(app)}
              onOpenLetter={onOpenLetter}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function statusOptionsFor(type: string | null): readonly string[] {
  return type === "building_control" ? BC_STATUS_OPTIONS : PLANNING_STATUS_OPTIONS;
}

function OpenApplicationCard({
  app,
  busy,
  editLoading,
  onStatus,
  onEdit,
  onClose,
  onOpenLetter,
}: {
  app: OpenApplicationRow;
  busy: boolean;
  editLoading: boolean;
  onStatus: (next: string) => void;
  onEdit: () => void;
  onClose: () => void;
  onOpenLetter: (id: number, source: LetterSource) => void;
}) {
  const typeLabel = workstreamTypeLabel(app.type ?? "");
  const job = app.job;
  const jobTitle = job ? job.job_name || job.full_address || "Untitled job" : null;
  // The address is inherited from the linked job (the canonical address holder);
  // application.site_address/site_postcode are immutable matching keys, used here
  // only as the orphan fallback when the app isn't linked to a job (job_id == 0).
  const siteAddress = job ? buildJobAddressString(job) : (app.site_address ?? "");
  const sitePostcode = job ? job.postcode : app.site_postcode;

  // Keep the current status selectable even if it's outside the standard list
  // (e.g. a create-time "draft"), so the select reflects reality.
  const baseOptions = statusOptionsFor(app.type);
  const statusOptions = app.status && !baseOptions.includes(app.status) ? [app.status, ...baseOptions] : baseOptions;

  return (
    <article className="job-app open-app">
      <header className="job-app__head">
        <div className="job-app__title">
          <span className="job-app__type">{typeLabel}</span>
          <span className="job-app__ref numeric">{app.app_reference || "Draft — no reference"}</span>
        </div>
        <div className="job-app__badges">
          {app.decision_outcome ? <Pill tone={decisionTone(app.decision_outcome)}>{humanise(app.decision_outcome)}</Pill> : null}
          {app.is_major ? <Pill tone="accent">Major</Pill> : null}
        </div>
      </header>

      <p className="open-app__job">
        {job ? (
          <Link to={`/staff/jobs/${job.id}`} className="open-app__job-link">
            <span className="numeric">{job.job_number}</span> — {jobTitle}
          </Link>
        ) : (
          <span className="job-drawer__muted">Not linked to a job</span>
        )}
      </p>

      {siteAddress || sitePostcode ? (
        <p className="open-app__address">
          {siteAddress ? <span>{siteAddress}</span> : null}
          {sitePostcode ? <span className="open-app__postcode">{sitePostcode}</span> : null}
        </p>
      ) : null}

      {app.local_authority_or_bcb ? <p className="job-app__lpa">{app.local_authority_or_bcb}</p> : null}
      {app.description ? <p className="job-app__desc">{app.description}</p> : null}

      <div className="job-app__status-row">
        <SelectField
          label="Status"
          className="job-app__status-field"
          value={app.status ?? ""}
          disabled={busy}
          onChange={(e) => onStatus(e.target.value)}
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{humanise(s)}</option>
          ))}
        </SelectField>
      </div>

      <dl className="job-app__dates">
        <DateRow label="Submitted" value={app.date_submitted} />
        <DateRow label="Validated" value={app.date_validated} />
        <DateRow label="Decision" value={app.date_decision} />
      </dl>

      {app.date_statutory_deadline ? (
        <div className="job-app__deadline">
          <span className="job-drawer__muted">Statutory deadline: {formatDateShort(app.date_statutory_deadline)}</span>
          <DeadlineChipView daysRemaining={app.days_to_statutory} />
        </div>
      ) : null}

      {app.letter_count > 0 ? (
        <div className="job-app__letters">
          <AppLetters applicationId={app.id} onOpen={onOpenLetter} />
        </div>
      ) : null}

      {/* Every row here is open (the feed only lists open apps), so the gate turns
          on type + having a reference to search + decided-status + no view_url yet. */}
      {isPlanWireRelevant({ type: app.type, status: app.status, is_open: true, app_reference: app.app_reference, application_view_url: app.application_view_url }) ? (
        <PlanWirePanel app={app} />
      ) : null}

      {/* These cards are narrower than the job-detail ones, so keep Edit/Close on
          one row and drop the council-portal link onto its own row beneath — all
          three as the same text-link style. */}
      <div className="job-app__actions open-app__actions">
        <div className="open-app__action-row">
          {job ? (
            <button type="button" className="job-app__link" disabled={busy || editLoading} onClick={onEdit}>
              {editLoading ? "Opening…" : "Edit application"}
            </button>
          ) : null}
          <button type="button" className="job-app__link" disabled={busy || editLoading} onClick={onClose}>
            Close application
          </button>
        </div>
        {app.application_view_url ? (
          <a
            className="job-app__link"
            href={app.application_view_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on council portal ↗
          </a>
        ) : null}
      </div>
    </article>
  );
}

function DateRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{formatDateShort(value)}</dd>
    </div>
  );
}
